/*
Species detail screen — fiche-espèce. Shows identity (FR common name +
latin binomial), Paris-wide count, Wikipedia summary if available, top
arrondissements, and the user's photo gallery for the species.

Mirrors Android SpeciesDetailScreen.kt. Skipped vs. Android (skeleton
scope): the species mini-map filtered to this `sk`. Add later by spinning
up a second MapLibre instance with a runtime filter on the source.
*/

import { navigate, kPaths, type Route } from '../router'
import { ensureSpeciesIndex, speciesNameFor, type SpeciesName } from '../data/speciesIndex'
import { ensureSpeciesInfo, speciesInfoFor, type SpeciesEntry } from '../data/speciesInfo'
import { listCaptures } from '../data/captureRepository'
import { loadPhoto } from '../data/photoStore'
import { ensureArbresFeatures } from '../data/arbresFeatures'
// Type-only import for the mini-map controller — `SpeciesMiniMap.ts`
// statically imports `maplibre-gl`, so pulling it in eagerly would
// drag the ~600 KB MapLibre runtime into the entry chunk and undo
// the MapView code-split. The runtime import below puts it in the
// shared MapLibre chunk instead.
import type { mountSpeciesMiniMap as MountSpeciesMiniMapFn } from './components/SpeciesMiniMap'
import { openLightbox, type LightboxPhoto } from './components/PhotoLightbox'
import { renderSkeletonBlock, renderSkeletonLines } from './components/Skeleton'
import { openRoutePlanSheet } from './components/RoutePlanSheet'
import { planRoute } from '../data/routePlanner'
import { setActiveRoute } from '../data/routeState'
import { getLatestFix } from '../capture/geolocation'
import type { Capture } from '../data/types'

const kFrenchDate = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
const kFrenchNumber = new Intl.NumberFormat('fr-FR')
const kPercentFr = new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 2 })

interface ViewModel
{
	speciesName: SpeciesName | null
	speciesInfo: SpeciesEntry | null
	captures: Capture[]
	loadError: string | null
}

let fActiveObjectUrls: string[] = []  // photo blob URLs created by the current mount, revoked on remount
let fActiveMiniMap: { destroy: () => void } | null = null  // mounted MapLibre instance for the species, destroyed on remount

/*
Mount the species detail screen for the route's `sk`. Renders a static
shell synchronously, hydrates async, then lazily loads photos.
*/
export function mountSpeciesScreen(inRoot: HTMLElement, inRoute: Route): void
{
	revokeObjectUrls()  // clean up any URLs left by the previous mount
	if (fActiveMiniMap) { fActiveMiniMap.destroy(); fActiveMiniMap = null }

	if (inRoute.id !== 'species')
	{
		inRoot.innerHTML = '<div class="screen"><div class="placeholder">Route invalide.</div></div>'
		return
	}

	const vSk = inRoute.sk
	const vCelebrate = inRoute.celebrate === true
	const vHighlight = typeof inRoute.highlight === 'number' ? inRoute.highlight : null

	inRoot.innerHTML = renderShell(vCelebrate)
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => history.length > 1 ? history.back() : navigate(kPaths.arboretum()))
	void hydrate(inRoot, vSk, vHighlight)
}

function renderShell(inCelebrate: boolean): string
{
	const vBanner = inCelebrate
		? '<div class="celebration-banner">✨ Nouvelle espèce capturée !</div>'
		: ''
	// Skeleton mirrors the populated layout's stack — identity card,
	// stats card, mini-map card, wiki card. Same vertical rhythm so
	// the page doesn't reflow when content arrives.
	const vSkeleton = `
		<section class="species-card species-card-skeleton">
			<div class="species-skeleton-identity">
				${renderSkeletonBlock({ width: '60px', height: '60px', radius: '50%' })}
				<div class="species-skeleton-identity-text">
					${renderSkeletonBlock({ width: '70%', height: '20px' })}
					${renderSkeletonBlock({ width: '45%', height: '13px' })}
				</div>
			</div>
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '38%', height: '15px' })}
			<div class="species-skeleton-stack">
				${renderSkeletonLines(2, '12px')}
			</div>
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '100%', height: '180px', radius: '12px' })}
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '32%', height: '15px' })}
			<div class="species-skeleton-stack">
				${renderSkeletonLines(3, '12px')}
			</div>
		</section>
	`
	return `
		<div class="screen species">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Espèce</h1>
			</header>
			${vBanner}
			<div class="species-body" id="species-body">
				${vSkeleton}
			</div>
		</div>
	`
}

async function hydrate(inRoot: HTMLElement, inSk: number, inHighlightId: number | null): Promise<void>
{
	const vBody = inRoot.querySelector<HTMLElement>('#species-body')
	if (!vBody) return

	const vModel = await loadViewModel(inSk)  // gather everything before painting once

	if (vModel.loadError)
	{
		vBody.innerHTML = `<div class="placeholder species-error">⚠ ${escapeHtml(vModel.loadError)}</div>`
		return
	}

	vBody.innerHTML = renderViewModel(inSk, vModel)
	wireRouteCta(inRoot, vModel)
	void hydrateGallery(inRoot, vModel.captures, inHighlightId)
	void hydrateMiniMap(inRoot, inSk)
}

/*
Wire the "Générer un itinéraire" button. Opens the picker; on
distance pick, runs the planner against the latest fix + features
index and pushes the plan into routeState before navigating to
the map. We resolve the species label here (rather than re-derive
inside the picker) since the screen already has the loaded
SpeciesName.
*/
function wireRouteCta(inRoot: HTMLElement, inModel: ViewModel): void
{
	const vBtn = inRoot.querySelector<HTMLButtonElement>('#species-route-cta')
	if (!vBtn) return
	vBtn.addEventListener('click', () => {
		const vSk = Number(vBtn.dataset.sk)
		if (!Number.isFinite(vSk)) return
		const vFix = getLatestFix()
		const vLabel = inModel.speciesName?.commonName ?? formatLatin(inModel.speciesName)
		openRoutePlanSheet({
			speciesLabel: vLabel,
			hasFix: vFix !== null,
			onPick: (inMeters) => { void onRouteDistancePicked(vSk, inMeters) },
		})
	})
}

async function onRouteDistancePicked(inSk: number, inMeters: number): Promise<void>
{
	const vFix = getLatestFix()
	if (!vFix) return  // sheet was supposed to disable the button when no fix; defensive
	try
	{
		const vFeatures = await ensureArbresFeatures()
		const vPlan = planRoute({
			startLat: vFix.lat,
			startLon: vFix.lon,
			speciesIndex: inSk,
			targetMeters: inMeters,
			allFeatures: vFeatures,
		})
		if (!vPlan) return  // species has no candidates anywhere — silently no-op
		setActiveRoute(vPlan)
		navigate(kPaths.map())
	}
	catch (vErr)
	{
		console.warn('Route planning failed', vErr)
	}
}

/*
Mount the species mini-map after the body is in the DOM (the container
needs to exist + be sized). Skipped when the species has no features —
typically only happens if the dataset is partial / out-of-sync. The
MapLibre-bearing module is loaded dynamically so it ends up in the
shared MapLibre chunk rather than the entry bundle.
*/
async function hydrateMiniMap(inRoot: HTMLElement, inSk: number): Promise<void>
{
	const vContainer = inRoot.querySelector<HTMLElement>('#species-mini-map')
	if (!vContainer) return
	try
	{
		await ensureArbresFeatures()  // populates the in-memory FeatureCollection
		const vModule = await import('./components/SpeciesMiniMap')
		const vMount: typeof MountSpeciesMiniMapFn = vModule.mountSpeciesMiniMap
		const vController = vMount(vContainer, inSk)
		if (vController) fActiveMiniMap = vController
	}
	catch (vErr)
	{
		console.warn('Mini-map mount failed', vErr)
	}
}

async function loadViewModel(inSk: number): Promise<ViewModel>
{
	try
	{
		const [, , vAllCaptures] = await Promise.all([
			ensureSpeciesIndex(),
			ensureSpeciesInfo(),
			listCaptures(),
		])
		const vCaptures = vAllCaptures.filter((inC) => inC.speciesIndex === inSk)  // user's photos for this species
		return {
			speciesName: speciesNameFor(inSk),
			speciesInfo: speciesInfoFor(inSk),
			captures: vCaptures,
			loadError: null,
		}
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		return { speciesName: null, speciesInfo: null, captures: [], loadError: vMsg }
	}
}

function renderViewModel(inSk: number, inModel: ViewModel): string
{
	const vTitle = inModel.speciesName?.commonName ?? formatLatin(inModel.speciesName)
	const vSubtitle = inModel.speciesName?.commonName ? formatLatin(inModel.speciesName) : ''
	const vInfo = inModel.speciesInfo
	const vWikiSummary = vInfo?.summary && vInfo.summary.length > 0 ? vInfo.summary : null
	const vWikiSlug = vInfo?.wp && vInfo.wp.length > 0 ? vInfo.wp : null
	const vPdf = vInfo?.pdf && vInfo.pdf.length > 0 ? vInfo.pdf : null

	const vIdentity = `
		<section class="species-card">
			<div class="species-glyph" aria-hidden="true">🌳</div>
			<div class="species-name">
				<h2 class="display species-title">${escapeHtml(vTitle)}</h2>
				${vSubtitle ? `<div class="species-latin"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
			</div>
		</section>
	`

	const vStats = vInfo ? renderStats(vInfo) : ''
	const vWiki = vWikiSummary ? renderWiki(vWikiSummary, vWikiSlug) : ''
	const vPdfRow = vPdf ? `
		<section class="species-card">
			<a class="species-link" href="${escapeAttr(vPdf)}" target="_blank" rel="noopener">
				📄 Fiche essence Ville de Paris
			</a>
		</section>
	` : ''

	const vGallery = renderGalleryShell(inSk, inModel.captures.length)
	const vMap = `
		<section class="species-card species-card-map">
			<h3 class="species-section-title">Sur la carte</h3>
			<div class="species-mini-map" id="species-mini-map"></div>
		</section>
	`

	const vRouteCta = `
		<section class="species-card species-card-route">
			<button class="species-route-button" id="species-route-cta" type="button" data-sk="${inSk}">
				<span class="species-route-button-glyph" aria-hidden="true">🚶</span>
				<span class="species-route-button-body">
					<span class="species-route-button-title">Générer un itinéraire</span>
					<span class="species-route-button-subtitle">Une promenade de 1–5&nbsp;km enchaînant cette espèce.</span>
				</span>
				<span class="species-route-button-chevron" aria-hidden="true">›</span>
			</button>
		</section>
	`

	return `
		${vIdentity}
		${vGallery}
		${vStats}
		${vMap}
		${vRouteCta}
		${vWiki}
		${vPdfRow}
	`
}

function renderStats(inInfo: SpeciesEntry): string
{
	const vCount = kFrenchNumber.format(inInfo.stats.count)
	const vProp = kPercentFr.format(inInfo.stats.proportion)
	const vMedHm = inInfo.stats.medianHm  // median height in meters, may be null
	const vMedCm = inInfo.stats.medianCircCm  // median circumference in cm, may be null
	const vTopArr = inInfo.stats.topArrAbs.slice(0, 3)
	const vBars = vTopArr.map((inA) => {
		const vMaxCount = vTopArr[0]?.count ?? 1  // first row is the absolute leader
		const vPct = (inA.count / vMaxCount) * 100  // bar width relative to leader
		return `
			<div class="species-bar-row">
				<span class="species-bar-label">${escapeHtml(inA.arr)}</span>
				<span class="species-bar-track"><span class="species-bar-fill" style="width:${vPct.toFixed(0)}%"></span></span>
				<span class="species-bar-count">${kFrenchNumber.format(inA.count)}</span>
			</div>
		`
	}).join('')

	const vMedians: string[] = []  // median rows shown only when the build dataset has values for this species
	if (typeof vMedHm === 'number' && vMedHm > 0)
	{
		vMedians.push(`<div class="species-stat-line">📏 Hauteur médiane : <strong>${vMedHm} m</strong></div>`)
	}
	if (typeof vMedCm === 'number' && vMedCm > 0)
	{
		vMedians.push(`<div class="species-stat-line">⭕ Circonférence médiane : <strong>${vMedCm} cm</strong></div>`)
	}

	return `
		<section class="species-card">
			<h3 class="species-section-title">À Paris</h3>
			<div class="species-stat-line">
				<strong>${vCount}</strong> arbres · <strong>${vProp}</strong> du dataset
			</div>
			${vMedians.join('')}
			${vBars ? `<div class="species-bars">${vBars}</div>` : ''}
		</section>
	`
}

function renderWiki(inSummary: string, inSlug: string | null): string
{
	const vLink = inSlug ? `
		<a class="species-link" href="https://fr.wikipedia.org/wiki/${encodeURIComponent(inSlug)}" target="_blank" rel="noopener">
			Lire sur Wikipedia →
		</a>
	` : ''
	return `
		<section class="species-card species-card-wiki">
			<h3 class="species-section-title">Wikipédia</h3>
			<p class="species-wiki-text">${escapeHtml(inSummary)}</p>
			${vLink}
			<div class="species-wiki-attribution">Source : Wikipédia FR · CC BY-SA 4.0</div>
		</section>
	`
}

function renderGalleryShell(_inSk: number, inCaptureCount: number): string
{
	if (inCaptureCount === 0)
	{
		return `
			<section class="species-card species-empty-gallery">
				<h3 class="species-section-title">Tes captures</h3>
				<p class="species-section-blurb">
					Pas encore de capture pour cette espèce. Reviens après l'avoir
					rencontrée sur la carte.
				</p>
			</section>
		`
	}
	return `
		<section class="species-card">
			<h3 class="species-section-title">Tes captures · ${inCaptureCount}</h3>
			<div class="species-gallery" id="species-gallery"></div>
		</section>
	`
}

/*
Async-load photo Blobs for the user's captures of this species and
inject <img> tags. Object URLs are tracked in module scope so we can
revoke them on next mount, avoiding the IDB Blob memory leak that creeps
up in long-lived single-page apps.

If `inHighlightId` matches one of the rendered captures (handed off
from the profile timeline through the route's `highlight` query
param), that thumbnail is wrapped with `data-highlighted="true"` so
the CSS pulse can fire, and the gallery scrolls it into view.
*/
async function hydrateGallery(
	inRoot: HTMLElement,
	inCaptures: Capture[],
	inHighlightId: number | null,
): Promise<void>
{
	const vGallery = inRoot.querySelector<HTMLElement>('#species-gallery')
	if (!vGallery) return
	const vPhotos: LightboxPhoto[] = []  // accumulated for the lightbox handler — mirrors the thumbnail order
	let vHighlightedImg: HTMLImageElement | null = null  // resolved at most once, scrolled into view after the loop
	for (const vCap of inCaptures)
	{
		if (!vCap.photoFilename) continue
		const vBlob = await loadPhoto(vCap.photoFilename)
		if (!vBlob) continue
		const vUrl = URL.createObjectURL(vBlob)  // ephemeral, revoked on next mount
		fActiveObjectUrls.push(vUrl)
		const vCaption = `Capture du ${kFrenchDate.format(new Date(vCap.timestamp))}`
		const vImg = document.createElement('img')
		vImg.className = 'species-thumb'
		vImg.alt = vCaption
		vImg.src = vUrl
		vImg.loading = 'lazy'
		if (inHighlightId !== null && vCap.id === inHighlightId)
		{
			vImg.dataset.highlighted = 'true'  // CSS pulse keys off this attribute
			vHighlightedImg = vImg
		}
		const vIndexInGallery = vPhotos.length  // freeze the lightbox index for this thumb's click handler
		vImg.addEventListener('click', () => { openLightbox(vPhotos, vIndexInGallery) })
		vGallery.appendChild(vImg)
		vPhotos.push({ url: vUrl, caption: vCaption })
	}

	// After the gallery is fully painted, scroll the highlighted
	// thumbnail into view. Wait one rAF so the layout has settled.
	if (vHighlightedImg)
	{
		const vTarget = vHighlightedImg
		requestAnimationFrame(() => {
			try { vTarget.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }) }
			catch { vTarget.scrollIntoView() }  // older browsers fall back to no-options form
		})
	}
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
